//LIBRARIES
import React, { Component, Fragment } from 'react';
import Auth from 'j-toker';
import { connect } from 'react-redux';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown/with-html';
import FlatButton from 'material-ui/FlatButton';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import withMobileDialog from '@material-ui/core/withMobileDialog';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { localizePrivacyStatementLink } from 'helpers/Localization';
import { featureEnabled } from 'helpers/ToggleBot';

//COMPONENTS
import LeadInitialPreferencesForm from 'navigation/LeadInitialPreferencesForm';
import { getUserNotificationPreferences } from 'actions/userTypesActions';

//ASSETS
import lockIcon from 'assets/lock_icon.png';
import phoneIcon from 'assets/phone_icon.png';

//ACTIONS
import { saveInitialUserPreference } from 'actions/tenantProfileActions';

const StyledMarkdown = styled(ReactMarkdown)`
  &h4 {
    textalign: 'justify';
    marginbottom: '15px';
  }
  &p {
    textalign: 'justify';
    marginbottom: '15px';
  }
`;

const StyledIcon = styled.img`
  width: 18px;
  display: 'inline-flex';
`;

const StyledPhoneIcon = styled.img`
  width: 25px;
  display: 'inline-flex';
`;

const StyledFormControlLabel = styled(FormControlLabel)`
  float: left;
  margin: 0;
  text-transform: none;
`;

/**
 * 1) TERMS_AND_CONDITIONS
 *
 * if not accepted:
 * 2) EMAIL_LOGOUT_WARNING_STEP
 * 3) END
 *
 * if accepted and notifications are hidden:
 * 2) EMAIL_PHONE_NUMBER_FORM_STEP
 * 3) END
 *
 * if accepted and notifications are not hidden:
 * 2) NOTIFICATION_FORM_STEP
 *
 * when notification form is filled out:
 * 3) EMAIL_PHONE_NUMBER_FORM_STEP
 * 4) END
 */
export const TERMS_AND_CONDITIONS_STEP = 'terms_and_conditions';
export const NOTIFICATION_FORM_STEP = 'notification_form';
export const EMAIL_PHONE_NUMBER_FORM_STEP = 'email_phone_number_form';
export const EMAIL_LOGOUT_WARNING_STEP = 'email_logout_warning';

class LeadInitialization extends Component {
  constructor(props) {
    super(props);
    this.state = {
      terms: null,
      activeStep: TERMS_AND_CONDITIONS_STEP,
      hasAgreedToTermsOfUse: false,
    };
    this.formRef = React.createRef();
  }

  requiredFileExists = (locale, termsFileName) => {
    try {
      return require(`assets/localization/tenantPortal/${locale}.${termsFileName}.md`);
    } catch (e) {
      return require(`assets/localization/tenantPortal/${locale}.default.termsConditions.md`);
    }
  };

  componentDidMount() {
    this.fetchTerms();
  }

  fetchTerms = () => {
    const { termsFileName } = this.props;
    const locale = getCurrentLocale();

    const termsConditionsMD = this.requiredFileExists(locale, termsFileName);
    fetch(termsConditionsMD.default ?? termsConditionsMD)
      .then((response) => response.text())
      .then((text) => {
        const privacyStatement = localizePrivacyStatementLink(
          this.props.privacyStatementLink,
          locale
        );
        const landlordName = this.props.landlordName
          ? this.props.landlordName.toUpperCase()
          : 'YUHU';
        this.setState({
          terms: text
            .replace('{privacyStatement}', privacyStatement)
            .replace('{landlordName}', landlordName),
        });
      });
  };

  acceptTerms = async () => {
    if (!this.state.hasAgreedToTermsOfUse) {
      return;
    }

    if (featureEnabled('ui.residents.notification_preferences.opt_out_hide_initial_screen')) {
      this.setState({
        activeStep: EMAIL_PHONE_NUMBER_FORM_STEP,
      });
    } else {
      await this.props.getUserNotificationPreferences();
      this.setState({
        activeStep: NOTIFICATION_FORM_STEP,
      });
    }
  };

  toggleAgreeToTermsOfUse = () => {
    this.setState({ hasAgreedToTermsOfUse: !this.state.hasAgreedToTermsOfUse });
  };

  handleInitialNotificationFormSubmit = (data) => {
    const emailChanged = Auth.user.email !== data.email;
    if (this.state.activeStep === 'email_phone_number_form' && emailChanged) {
      this.setState({ activeStep: EMAIL_LOGOUT_WARNING_STEP });
      return;
    }
    this.props
      .saveInitialUserPreference(data, emailChanged)
      .catch((error) => this.setState({ activeStep: EMAIL_PHONE_NUMBER_FORM_STEP }));
  };

  render() {
    const { mustAcceptTerms, initialDialogOpen, currentRoute } = this.props;
    const { activeStep } = this.state;

    if (!mustAcceptTerms || !initialDialogOpen) return null;
    if (currentRoute === '/tenant_application') return null;

    let icon = null;
    let title = getTranslatedString('tenantPortal.leadInitialization.termsOfUse');
    let content = null;

    if (activeStep === TERMS_AND_CONDITIONS_STEP) {
      icon = <StyledIcon src={lockIcon} alt="loader" />;
      title = getTranslatedString('tenantPortal.leadInitialization.termsOfUse');
      content = (
        <p style={{ margin: '1px 0 0 0', fontSize: '10px', textAlign: 'center' }}>
          {getTranslatedString('tenantPortal.leadInitialization.termsEffectiveDate')}
          <br />
          {getTranslatedString('tenantPortal.leadInitialization.yourPrivacy')}
        </p>
      );
    } else if (activeStep === NOTIFICATION_FORM_STEP) {
      icon = <StyledIcon src={lockIcon} alt="loader" />;
      title = getTranslatedString('tenantPortal.leadInitialization.notificationPreferences');
      content = null;
    } else if (activeStep === EMAIL_PHONE_NUMBER_FORM_STEP) {
      icon = <StyledPhoneIcon src={phoneIcon} alt="loader" />;
      title = getTranslatedString('residentPortal.leadInitialization.contactDetails');
      content = (
        <p
          style={{
            margin: '1px 0 0 0',
            fontSize: '10px',
            textAlign: 'center',
            lineHeight: '1.5',
          }}
        >
          {getTranslatedString('residentPortal.leadInitialization.confirmThisIsBestWay')}
          <br />
          {getTranslatedString('residentPortal.leadInitialization.pleaseNote')}
        </p>
      );
    } else if (activeStep === EMAIL_LOGOUT_WARNING_STEP) {
      title = getTranslatedString('residentPortal.leadInitialization.headsUp');
      icon = <StyledIcon src={lockIcon} alt="loader" />;
      content = (
        <p
          style={{
            margin: '10px 0 0 0',
            fontSize: '10px',
            textAlign: 'center',
            lineHeight: '1.5',
            width: '70%',
          }}
        >
          {getTranslatedString('residentPortal.leadInitialization.itLooksLikeEmailChanged')}
        </p>
      );
    }

    return (
      <Dialog
        fullScreen={this.props.fullScreen}
        open={initialDialogOpen}
        aria-labelledby="lead-intialization"
      >
        <DialogTitle style={{ paddingBottom: '0px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {icon && <div style={{ textAlign: 'center', margin: '20px' }}>{icon}</div>}
            <h3 style={{ textAlign: 'center', margin: 0 }}>{title}</h3>
            {content}
          </div>
          {activeStep !== EMAIL_LOGOUT_WARNING_STEP && (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                margin: '25px',
              }}
            >
              <div style={{ borderTop: '2px solid #d8d8d8', width: '30%' }} />
            </div>
          )}
        </DialogTitle>
        <DialogContent>
          {activeStep === TERMS_AND_CONDITIONS_STEP && this.state.terms && (
            <StyledMarkdown source={this.state.terms} escapeHtml={false} />
          )}
          {activeStep !== TERMS_AND_CONDITIONS_STEP && (
            <LeadInitialPreferencesForm
              onSubmit={(data) => this.handleInitialNotificationFormSubmit(data)}
              activeStep={activeStep}
              formRef={this.formRef}
            />
          )}
        </DialogContent>
        <DialogActions style={{ display: 'block' }}>
          <Fragment>
            {activeStep === TERMS_AND_CONDITIONS_STEP && this.state.terms && (
              <StyledFormControlLabel
                control={
                  <Checkbox
                    name="hasAgreedToTermsOfUse"
                    value={this.state.hasAgreedToTermsOfUse}
                    checked={this.state.hasAgreedToTermsOfUse}
                    onChange={this.toggleAgreeToTermsOfUse}
                  />
                }
                label={getTranslatedString('tenantPortal.leadInitialization.agreeToTermsOfUse')}
              />
            )}
            {this.renderButtons()}
          </Fragment>
        </DialogActions>
      </Dialog>
    );
  }

  renderButtons = () => {
    const { activeStep, hasAgreedToTermsOfUse } = this.state;
    const isDisabled = !hasAgreedToTermsOfUse;
    let onClick = this.acceptTerms;
    let onClickBack = null;
    let buttonText = getTranslatedString('continue');

    if (activeStep === NOTIFICATION_FORM_STEP) {
      onClick = () => this.setState({ activeStep: EMAIL_PHONE_NUMBER_FORM_STEP });
      buttonText = getTranslatedString('continue');
    } else if (activeStep === EMAIL_PHONE_NUMBER_FORM_STEP) {
      onClick = () => {
        this.formRef.current.handleSubmit();
      };
      onClickBack = () => this.setState({ activeStep: NOTIFICATION_FORM_STEP });
      buttonText = getTranslatedString('residentPortal.leadInitialization.confirmContactDetails');
    } else if (activeStep === EMAIL_LOGOUT_WARNING_STEP) {
      onClick = () => {
        this.formRef.current.handleSubmit();
      };
      onClickBack = () => this.setState({ activeStep: EMAIL_PHONE_NUMBER_FORM_STEP });
      buttonText = getTranslatedString('residentPortal.leadInitialization.confirmContactDetails');
    }

    return (
      <div>
        {!isDisabled && (
          <FlatButton
            variant="raised"
            onClick={onClick}
            style={{
              color: 'white',
              float: 'right',
              padding: '0px 15px',
              marginTop: '5px',
              backgroundColor: AccentColor(),
            }}
          >
            {buttonText}
          </FlatButton>
        )}
        {onClickBack && (
          <FlatButton
            variant="raised"
            onClick={onClickBack}
            style={{
              padding: '0px 15px',
              float: 'left',
              color: AccentColor(),
              backgroundColor: '#fff',
            }}
          >
            {getTranslatedString('back')}
          </FlatButton>
        )}
      </div>
    );
  };
}

LeadInitialization = connect(
  (state) => ({
    landlordName: state.userTypes.landlordName,
    userPreferences: state.userTypes.userPreferences,
    saveInitialUserPreferenceError: state.tenantProfile.saveInitialUserPreferenceError,
    termsFileName: state.userTypes.termsFileName,
  }),
  {
    saveInitialUserPreference,
    getUserNotificationPreferences,
  }
)(LeadInitialization);

export default withMobileDialog()(LeadInitialization);
